import { StatusCodes } from '@e3dc-react/shell/Libs/http-status-codes'
import axios from 'axios'
import { isUndefined } from 'lodash'
import { ApiResult } from './types'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'

type HttpStatusError = {
  response?: {
    status?: number
  }
  statusCode?: number
}

export const getHttpStatusCodeFromError = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined

  const { response, statusCode } = error as HttpStatusError
  return response?.status ?? statusCode
}

export const isAxiosInternalServerError = (error?: Error | AuthFetchError): boolean =>
  !isUndefined(error) && axios.isAxiosError(error) && Number((error as AuthFetchError).response?.status) === StatusCodes.INTERNAL_SERVER_ERROR

export const handleFetchError = <T>(error: unknown): ApiResult<T, AuthFetchError | Error> => {
  if (error instanceof Error || axios.isAxiosError(error)) {
    return new ApiResult<T, Error | AuthFetchError>(null, true, error as Error | AuthFetchError)
  }
  return new ApiResult<T, Error | AuthFetchError>(null, true)
}

/**
 * Logs the failure and rethrows, preferring the server-provided error message from the response body.
 * @throws {Error} with the server message (original error as `cause`), or the original error if none is available.
 */
export const throwApiError = (error: unknown, context: string): never => {
  console.error(context, error)

  if (axios.isAxiosError(error)) {
    const serverMessage = (error as AuthFetchError).response?.data?.error
    if (serverMessage) {
      throw new Error(serverMessage, { cause: error })
    }
  }

  throw error
}

/**
 * In general this repo's error handling allows errors of type Error or AuthFetchError.
 * This function returns true if its an AutFetchError and the status is 401, which means the user is unauthorized.
 *
 * This can e.g. be helpful if you don't want to show a snackbar for unauthorized requests but for other errors.
 */
export const isUnauthorizedAuthFetchError = (error?: Error | AuthFetchError): boolean => {
  return !!error && !!(error as AuthFetchError).response && (error as AuthFetchError)?.response?.status === StatusCodes.UNAUTHORIZED
}

/**
 * This is a generic wrapper function for queryFn in react-query that makes it work with our without ApiResult.
 * It checks if the result is an instance of ApiResult, and if so, it checks for errors and unwraps the value.
 * If it's not an ApiResult, it returns the result directly (or null if it's undefined).
 *
 * @param fetchFunction - the function to be called
 * @param errorMessage - optional custom error message
 * @returns - the value of the fetchFunction or null
 */
export const queryFnWithApiResult =
  <T, U extends unknown[]>(fetchFunction: (...args: U) => Promise<ApiResult<T> | T>, errorMessage = 'An unexpected error occurred when fetching') =>
  async (...args: U): Promise<T | null> => {
    const result = await fetchFunction(...args)

    // `@e3dc-react/shell/Types` exports a structurally identical but distinct `ApiResult` class.
    // Api functions used with usePortalQuery/usePortalMutation must construct their result with
    // THIS module's `ApiResult` (imported from 'src/Libs/api'), or this check silently fails and
    // the raw { value, hasErrors, error } wrapper leaks through as query data instead of unwrapping.
    if (result instanceof ApiResult) {
      if (result.hasErrors) throw result.error || new Error(errorMessage)
      return result.value
    }

    return result ?? null
  }
